import { Injectable } from '@angular/core';
import {
  DateFilterModel,
  FilterModel,
  IServerSideGetRowsRequest,
  ISimpleFilterModel,
  NumberFilterModel,
  SetFilterModel,
  TextFilterModel,
} from 'ag-grid-community';
import { TypesaurusQuery } from 'typesaurus';
import { FirestoreGenInterface } from './firestore.gen.service.interface';
import { AbstractQueryParams } from './query-params';
import {
  AllRequired,
  DocModel,
  KeysOfType,
  NeverIfEmptyDocModelShape,
  OffSubscriptionWithCatch,
  OrOrOrderQuery,
  OrderCursors,
  OrderDirection,
  PaginationStateExtension,
  QueryArrayField,
  QueryFieldKey1,
  QueryFieldValue,
  QueryPrimitiveField,
  TypesaurusCoreSubscriptionPromiseCallback as SaurusCoreSubPromCb,
  SaurusHelper,
  SaurusUnionVariableWideModelType,
  SaurusWriteDataWideWithProps,
  SaurusWriteHelpers,
  TypesaurusAssignArgDataModel,
  TypesaurusAssignArgDataWideModel,
  TypesaurusCoreCollection,
  TypesaurusCoreDoc,
  TypesaurusCoreDocDef,
  TypesaurusCoreRef,
  TypesaurusSchema,
  TypesaurusUpdateArgData,
  UnionKeys,
} from './typesaurus.interface';

/** The `QueryPrimitiveField` methods that take a single comparison value. */
type ComparisonOperator = 'lt' | 'lte' | 'eq' | 'not' | 'gt' | 'gte';

/**
 * The value an order cursor accepts for a field of `Def`, spelled out for the same
 * `Parent` / `Key` pair `QueryPrimitiveField<Def>` is built with.
 */
type OrderCursorValueOf<Def extends TypesaurusCoreDocDef> = TypesaurusQuery.OrderCursorValue<
  Def,
  Def['Model'],
  UnionKeys<AllRequired<Def['Model']>>
>;

/** ag-grid filter types that map one to one onto a single Typesaurus comparison. */
const comparisonByFilterType = new Map<ISimpleFilterModel['type'], ComparisonOperator>([
  ['lessThan', 'lt'],
  ['lessThanOrEqual', 'lte'],
  ['equals', 'eq'],
  ['notEqual', 'not'],
  ['greaterThan', 'gt'],
  ['greaterThanOrEqual', 'gte'],
  // `startsWith` is deliberately absent: it needs two constraints, so it is handled before this
  // lookup. `endsWith` is absent because Firestore cannot express it at all; it used to map to a
  // bare `lte`, which returns everything sorted at or before the term.
]);

/**
 * The highest code point in the Basic Multilingual Plane, and the conventional upper bound for a
 * Firestore prefix range: no ordinary string sorts above `term + this` while still starting with
 * `term`.
 */
const PREFIX_RANGE_SENTINEL = '\uf8ff';

/**
 * Operators the searchbar and AG Grid can both offer but Firestore cannot answer. A substring or
 * suffix match needs an index that stores the substrings, and there is no such representation on
 * any collection here.
 *
 * They are refused rather than approximated. The previous translations quietly returned something
 * else entirely (`contains` became an `in` over one value, i.e. exact equality), which is the worst
 * of the options: the grid shows a confident, wrong result set and nothing anywhere says so.
 */
const UNSUPPORTED_TEXT_FILTERS = new Set(['contains', 'notContains', 'endsWith']);

@Injectable({
  providedIn: 'root',
})
export abstract class AbstractGenFirestoreService<
  Sma extends TypesaurusSchema,
  SmaKey extends keyof Sma,
  Def extends TypesaurusCoreDocDef = Sma[SmaKey]['Def'],
> implements FirestoreGenInterface<Def> {
  private orm: TypesaurusCoreCollection<Def>;

  constructor(orm: TypesaurusCoreCollection<Def>) {
    this.orm = orm;
  }
  async Get(id: Sma[SmaKey]['Id']): Promise<TypesaurusCoreDoc<Def> | null> {
    return await this.orm.get(id);
  }
  OnGet(
    id: Sma[SmaKey]['Id'],
    callback: SaurusCoreSubPromCb<TypesaurusCoreDoc<Def> | null | undefined>,
  ): OffSubscriptionWithCatch {
    return this.orm.get(id).on(callback);
  }
  async GetAll(): Promise<TypesaurusCoreDoc<Def>[]> {
    return await this.orm.all();
  }
  async CountAll(): Promise<number> {
    return await this.orm.count();
  }
  OnGetAll(callback: SaurusCoreSubPromCb<Array<TypesaurusCoreDoc<Def> | null | undefined>>): OffSubscriptionWithCatch {
    return this.orm.all().on(callback);
  }
  OnQueryAll(
    runs: OrOrOrderQuery<Def>[],
    callback: SaurusCoreSubPromCb<Array<TypesaurusCoreDoc<Def> | null | undefined>>,
  ): OffSubscriptionWithCatch {
    return this.orm.query(() => runs).on(callback);
  }
  async QueryAll(queryHelpers: Array<AbstractQueryParams<Def>>): Promise<TypesaurusCoreDoc<Def>[]> {
    return await this.orm.query((q) =>
      queryHelpers.map((x: AbstractQueryParams<Def>) => {
        if (x.field?.length && x.field.length >= 1) {
          return this.selectFieldNonBuilder(q, x);
        }
        if (x?.orderType && x.orderTypeValue) {
          q.field('__id__').order(q[x.orderType](x.orderTypeValue));
        }
        if (x.orderDirection) {
          (q.field(x.orderBy as QueryFieldKey1<Def>) as QueryPrimitiveField<Def>).order(x?.orderDirection);
        }
        if (x.limit) {
          q.limit(x.limit);
        }
        return undefined;
      }),
    );
  }

  async QueryPaginateById(
    queryHelpers: IServerSideGetRowsRequest & PaginationStateExtension,
  ): Promise<{ transactions: TypesaurusCoreDoc<Def>[]; direction: string }> {
    let direction = 'desc';
    const queryBuilder = this.orm.query.build();

    /// filter section
    for (const [key, filterModelKey] of Object.entries(queryHelpers.filterModel ?? {})) {
      if (!(filterModelKey.conditions && filterModelKey.conditions?.length === filterModelKey.length)) {
        filterModelKey.conditions = [{ ...filterModelKey }];
      }
      if (filterModelKey?.operator === 'OR') {
        queryBuilder.or(
          filterModelKey.conditions.map(
            (element: DateFilterModel & TextFilterModel & NumberFilterModel & SetFilterModel) => {
              return this.filterTypeHelper(element, queryBuilder, key);
            },
          ),
        );
      } else {
        filterModelKey.conditions.forEach(
          (element: DateFilterModel & TextFilterModel & NumberFilterModel & SetFilterModel) =>
            this.filterTypeHelper(element, queryBuilder, key),
        );
      }
    }

    /// sort section
    if (queryHelpers?.endRow && typeof queryHelpers?.startRow === 'number') {
      const isFirstOrLast = queryHelpers.endRow >= queryHelpers.count || queryHelpers.startRow === 0;
      if (isFirstOrLast) {
        if (queryHelpers.sortModel.length) {
          queryHelpers.sortModel.forEach((sortEntry) => {
            this.columnField(queryBuilder, sortEntry.colId).order(sortEntry.sort);
          });
        } else {
          queryBuilder.field(queryBuilder.docId()).order(queryHelpers.startRow === 0 ? 'desc' : 'asc');
        }
        direction = queryHelpers.startRow === 0 ? 'desc' : 'asc';
      } else {
        const cursor = {
          direction: queryHelpers?.endRow && queryHelpers.data[queryHelpers?.endRow]?.ref?.id ? 'asc' : 'desc',
          startAtVal:
            queryHelpers?.endRow && queryHelpers.data[queryHelpers?.endRow]?.ref?.id
              ? queryHelpers.data[queryHelpers.endRow].ref.id
              : queryHelpers.data[queryHelpers.startRow].ref.id,
        };
        if (queryHelpers.sortModel.length) {
          queryHelpers.sortModel.forEach((sortEntry) => {
            const sortDir = cursor.direction === 'desc' ? sortEntry.sort : sortEntry.sort === 'asc' ? 'desc' : 'asc';
            return this.columnField(queryBuilder, sortEntry.colId).order(
              sortDir,
              queryBuilder.startAt(cursor.startAtVal),
            );
          });
        } else {
          queryBuilder
            .field(queryBuilder.docId())
            .order(cursor.direction as OrderDirection, queryBuilder.startAt(cursor.startAtVal));
        }
        direction = cursor.direction;
      }

      /// limit section
      let limit =
        (queryHelpers.endRow >= queryHelpers.count ? queryHelpers.count : queryHelpers.endRow) - queryHelpers.startRow;
      if (limit === 0) limit = 10;
      queryBuilder.limit(limit >= 102 ? 102 : limit + 2);
    } else {
      queryBuilder.limit(101);
    }

    /// return section
    const transactions = await queryBuilder.run();
    return {
      transactions:
        queryHelpers?.startRow && typeof queryHelpers?.startRow === 'number' && queryHelpers?.startRow > 0
          ? transactions.slice(1)
          : transactions,
      direction,
    };
  }
  async QueryPaginateCount(queryHelpers: IServerSideGetRowsRequest & PaginationStateExtension): Promise<number> {
    return await this.orm
      .query((helpers) => {
        const runs = [];

        for (const [key, filterModelKey] of Object.entries(queryHelpers.filterModel ?? {})) {
          filterModelKey.conditions = [{ ...filterModelKey }];
          if (filterModelKey?.operator === 'OR') {
            runs.push(
              helpers.or(
                filterModelKey.conditions.map(
                  (element: DateFilterModel & TextFilterModel & NumberFilterModel & SetFilterModel) => {
                    return this.filterTypeHelper(element, helpers, key);
                  },
                ),
              ),
            );
          } else {
            filterModelKey.conditions.forEach(
              (element: DateFilterModel & TextFilterModel & NumberFilterModel & SetFilterModel) =>
                this.filterTypeHelper(element, helpers, key).forEach((whereQuery) => runs.push(whereQuery)),
            );
          }
        }
        if (queryHelpers?.endRow && typeof queryHelpers?.startRow === 'number') {
          const isFirstOrLast = queryHelpers.endRow >= queryHelpers.count || queryHelpers.startRow === 0;
          if (isFirstOrLast) {
            if (queryHelpers.sortModel.length) {
              queryHelpers.sortModel.forEach((sortEntry) => {
                runs.push(this.columnField(helpers, sortEntry.colId).order(sortEntry.sort));
              });
            } else {
              helpers.field(helpers.docId()).order(queryHelpers.startRow === 0 ? 'desc' : 'asc');
            }
          } else {
            const cursor = {
              direction: queryHelpers?.endRow && queryHelpers.data[queryHelpers?.endRow]?.ref?.id ? 'asc' : 'desc',
              startAtVal:
                queryHelpers?.endRow && queryHelpers.data[queryHelpers?.endRow]?.ref?.id
                  ? queryHelpers.data[queryHelpers.endRow].ref.id
                  : queryHelpers.data[queryHelpers.startRow].ref.id,
            };
            if (queryHelpers.sortModel.length) {
              queryHelpers.sortModel
                .map((sortEntry) => {
                  const sortDir =
                    cursor.direction === 'desc' ? sortEntry.sort : sortEntry.sort === 'asc' ? 'desc' : 'asc';
                  return this.columnField(helpers, sortEntry.colId).order(sortDir, helpers.startAt(cursor.startAtVal));
                })
                .forEach((orderQuery) => runs.push(orderQuery));
            } else {
              runs.push(
                helpers
                  .field(helpers.docId())
                  .order(cursor.direction as OrderDirection, helpers.startAt(cursor.startAtVal)),
              );
            }
          }
        }
        return runs;
      })
      ?.count();
  }
  SetQuery(request: Partial<IServerSideGetRowsRequest & PaginationStateExtension>): {
    runs: OrOrOrderQuery<Def>[];
    direction: string;
  } {
    const queryBuilder = this.orm.query.build();
    const runs = [];
    for (const key in request?.filterModel) {
      if (!(request.filterModel as FilterModel)[key]) continue;
      const filterModelKey = (request.filterModel as FilterModel)[key];
      filterModelKey.conditions = [{ ...filterModelKey }];
      if (filterModelKey?.operator === 'OR') {
        runs.push(
          queryBuilder.or(
            filterModelKey.conditions.map(
              (element: DateFilterModel & TextFilterModel & NumberFilterModel & SetFilterModel) => {
                return this.filterTypeHelper(element, queryBuilder, key);
              },
            ),
          ),
        );
      } else {
        filterModelKey.conditions.forEach(
          (element: DateFilterModel & TextFilterModel & NumberFilterModel & SetFilterModel) =>
            this.filterTypeHelper(element, queryBuilder, key).forEach((whereQuery) => runs.push(whereQuery)),
        );
      }
    }
    let direction = 'asc';
    if (
      (request?.filterModel as FilterModel)?.['createdAt'] &&
      !request?.sortModel?.find((sortEntry) => sortEntry.colId === 'createdAt')
    ) {
      request?.sortModel?.push({
        colId: 'createdAt',
        sort: 'asc',
      });
    }
    if (request?.endRow && typeof request?.startRow === 'number') {
      const isFirstOrLast = request.endRow >= (request?.count ?? 0) || request.startRow === 0;
      if (isFirstOrLast) {
        if (request?.sortModel?.length) {
          request?.sortModel.forEach((sortEntry) => {
            const sortDir = request.startRow === 0 ? sortEntry.sort : sortEntry.sort === 'asc' ? 'desc' : 'asc';
            runs.push(
              (queryBuilder.field(sortEntry.colId as QueryFieldKey1<Def>) as QueryPrimitiveField<Def>).order(sortDir),
            );
          });
        } else if (!(request?.filterModel as FilterModel)?.['createdAt']) {
          runs.push(queryBuilder.field(queryBuilder.docId()).order(request.startRow === 0 ? 'desc' : 'asc'));
        }
        direction = request.startRow === 0 ? 'desc' : 'asc';
      } else {
        const cursor = {
          direction: request?.endRow && request?.data?.[request?.endRow] ? 'asc' : 'desc',
          startDocAt:
            request?.endRow && request?.data?.[request?.endRow]?.ref?.id
              ? request?.data?.[request.endRow]
              : request?.data?.[request.startRow],
        };
        runs.push(
          queryBuilder
            .field(queryBuilder.docId())
            .order(cursor.direction as OrderDirection, queryBuilder.startAt(cursor?.startDocAt?.ref.id)),
        );
        request?.sortModel
          ?.map((sortEntry, sortIndex) => {
            const sortDir = cursor.direction === 'desc' ? sortEntry.sort : sortEntry.sort === 'asc' ? 'desc' : 'asc';
            const fieldPath = sortEntry.colId.split('.', 4);
            let startDocAt = (cursor.startDocAt as Record<string, unknown>)[fieldPath[0]];
            for (let i = 1; i < fieldPath.length; i++) {
              startDocAt = (startDocAt as Record<string, unknown>)[fieldPath[i]];
            }
            const sortField = queryBuilder.field(sortEntry.colId as QueryFieldKey1<Def>) as QueryPrimitiveField<Def>;
            if (sortIndex === 0) {
              return sortField.order(
                sortDir,
                queryBuilder.startAt<Def['Model'], UnionKeys<AllRequired<Def['Model']>>>(
                  startDocAt as OrderCursorValueOf<Def>,
                ),
              );
            }
            return sortField.order(sortDir);
          })
          .forEach((orderQuery) => runs.push(orderQuery));
        direction = cursor.direction;
      }
    }
    return { runs: runs.filter(this.removeSameKeyOrders), direction };
  }

  private removeSameKeyOrders(x: OrOrOrderQuery<Def>, _i: number, a: OrOrOrderQuery<Def>[]): boolean {
    if (x?.type === 'order') {
      return !(x.field.includes('__id__') && a.filter((x) => x.type === 'order').length > 1);
    }
    return true;
  }

  SetLimit(request: IServerSideGetRowsRequest & PaginationStateExtension): OrOrOrderQuery<Def>[] {
    const runs = [];
    const queryBuilder = this.orm.query.build();
    const rowCount =
      (request?.endRow ?? 0) >= request.count
        ? (request.count ?? 0) - (request.startRow ?? 0)
        : (request.endRow ?? 0) - (request.startRow ?? 0) + 1;

    runs.push(queryBuilder.limit(rowCount));
    return runs;
  }
  async GetBuildedCount(runs: OrOrOrderQuery<Def>[]): Promise<number> {
    return await this.orm.query(() => runs).count();
  }
  async GetBuildedData(runs: OrOrOrderQuery<Def>[]): Promise<TypesaurusCoreDoc<Def>[]> {
    return await this.orm.query(() => runs);
  }
  OnGetBuildedData(
    runs: OrOrOrderQuery<Def>[],
    callback: (arrayOfObjects: Array<TypesaurusCoreDoc<Def>>) => void,
  ): OffSubscriptionWithCatch {
    return this.orm.query(() => runs).on(callback);
  }

  async QuerySum(queryHelpers: KeysOfType<DocModel<Def>, number>): Promise<number> {
    return await this.orm.sum(queryHelpers);
  }

  async QuerySumByCreationDate(field: string, extraQuery: OrOrOrderQuery<Def>[]): Promise<number> {
    return await this.orm.query(() => [...extraQuery])?.sum(field as KeysOfType<DocModel<Def>, number>);
  }

  async QueryCountByCreationDate(
    field: string,
    value: number | string | boolean | Date,
    extraQuery: OrOrOrderQuery<Def>[],
  ): Promise<number> {
    return await this.orm
      .query((helpers) => [...extraQuery, this.columnField(helpers, field).not(value as QueryFieldValue<Def>)])
      ?.count();
  }

  async ManyById(IdArray: Sma[SmaKey]['Id'][]): Promise<(TypesaurusCoreDoc<Def> | null)[]> {
    return await this.orm.many(IdArray);
  }

  async Create(assignData: TypesaurusAssignArgDataModel<Def>): Promise<TypesaurusCoreRef<Def>> {
    return await this.orm.add(
      ($: SaurusWriteHelpers<SaurusUnionVariableWideModelType<Def>>) =>
        ({
          ...assignData,
          createdAt: $.serverDate(),
        }) as SaurusWriteDataWideWithProps<SaurusUnionVariableWideModelType<Def>>,
    );
  }

  async Set(ref: TypesaurusCoreRef<Def>, data: TypesaurusAssignArgDataWideModel<Def>): Promise<TypesaurusCoreRef<Def>> {
    return await ref.set(
      ($: SaurusWriteHelpers<SaurusUnionVariableWideModelType<Def>>) =>
        ({
          ...data,
          createdAt: $.serverDate(),
        }) as SaurusWriteDataWideWithProps<SaurusUnionVariableWideModelType<Def>>,
    );
  }

  async Update(ref: TypesaurusCoreRef<Def>, updateArg: TypesaurusUpdateArgData<Def>): Promise<TypesaurusCoreRef<Def>> {
    return await ref.update(($) => ({ ...(updateArg as TypesaurusUpdateArgData<Def>), updatedAt: $.serverDate() }));
  }

  async UpdateOrSet(
    ref: TypesaurusCoreRef<Def>,
    data: TypesaurusAssignArgDataWideModel<Def>,
  ): Promise<TypesaurusCoreRef<Def>> {
    return await ref.upset(data);
  }

  async Delete(ref: TypesaurusCoreRef<Def>): Promise<TypesaurusCoreRef<Def>> {
    return await ref.remove();
  }

  async BatchDelete(refs: TypesaurusCoreRef<Def>[]): Promise<TypesaurusCoreRef<Def>[]> {
    return await Promise.all(refs.map((ref) => ref.remove()));
  }

  async SoftDelete(ref: TypesaurusCoreRef<Def>): Promise<TypesaurusCoreRef<Def>> {
    const updateBuilder = ref.update.build();
    const timestampField = updateBuilder.field('createdAt' as keyof NeverIfEmptyDocModelShape<Def>);
    timestampField.set(updateBuilder.serverDate() as Parameters<typeof timestampField.set>[0]);
    return await updateBuilder.run();
  }

  private filterTypeHelper(
    element: DateFilterModel & TextFilterModel & NumberFilterModel & SetFilterModel,
    helpers: SaurusHelper<Def>,
    fields: string,
  ) {
    const field = this.columnField(helpers, fields);
    if (element.filterType === 'set') {
      return element.values.length ? [field.in(element.values as QueryFieldValue<Def>[])] : [];
    }

    if (UNSUPPORTED_TEXT_FILTERS.has(element.type as string)) {
      throw new Error(
        `Firestore cannot answer a "${element.type}" filter on "${fields}". ` +
          'Use "equals" or "startsWith", or filter this grid in memory.',
      );
    }
    // Before the generic comparison lookup, because `startsWith` needs two constraints and the map
    // can only express one. It used to resolve to a bare `gte`, which matches everything sorted at
    // or after the term rather than everything beginning with it.
    if (element.type === 'startsWith') {
      return this.prefixFilterHelper((element?.dateFrom ?? element?.filter) as string, field);
    }

    const comparison = comparisonByFilterType.get(element.type);
    if (comparison) {
      return [field[comparison]((element?.dateFrom ?? element?.filter) as QueryFieldValue<Def>)];
    }
    return this.matchFilterHelper(element, field);
  }

  /**
   * The only text search Firestore can actually answer: an ordered range from the term to the term
   * plus the highest code point, which is exactly the set of values beginning with it.
   */
  private prefixFilterHelper(prefix: string, field: QueryPrimitiveField<Def> & QueryArrayField<Def>) {
    return [
      field.gte(prefix as QueryFieldValue<Def>),
      field.lte(`${prefix}${PREFIX_RANGE_SENTINEL}` as QueryFieldValue<Def>),
    ];
  }

  private matchFilterHelper(
    element: DateFilterModel & TextFilterModel & NumberFilterModel,
    field: QueryPrimitiveField<Def> & QueryArrayField<Def>,
  ) {
    switch (element.type) {
      case 'inRange':
        return this.inRangeFilterHelper(element, field);
      case 'notBlank':
        return [field.notIn([null, undefined, ''] as QueryFieldValue<Def>[])];
      case 'blank':
      case 'empty':
        return [field.in(['', 'null', null, undefined] as QueryFieldValue<Def>[])];
      default:
        return [];
    }
  }

  private inRangeFilterHelper(
    element: DateFilterModel & TextFilterModel & NumberFilterModel,
    field: QueryPrimitiveField<Def> & QueryArrayField<Def>,
  ) {
    if (element?.dateFrom && element?.dateTo) {
      return [
        field.gte(new Date(element?.dateFrom) as QueryFieldValue<Def>),
        field.lte(new Date(element?.dateTo) as QueryFieldValue<Def>),
      ];
    }
    return [field.gte(element?.filter as QueryFieldValue<Def>), field.lte(element?.filterTo as QueryFieldValue<Def>)];
  }

  /**
   * Bridges an ag-grid column key, only known at runtime, onto the typed field selector.
   * A dotted key is handed over whole: typesaurus joins the rest arguments with a dot, so
   * `field('a.b')` and `field('a', 'b')` both resolve to the Firestore path `a.b`.
   */
  private columnField(helpers: SaurusHelper<Def>, field: string): QueryPrimitiveField<Def> & QueryArrayField<Def> {
    return helpers.field(field as QueryFieldKey1<Def>) as QueryPrimitiveField<Def> & QueryArrayField<Def>;
  }

  private selectFieldNonBuilder(
    helpers: SaurusHelper<Def>,
    params: AbstractQueryParams<Def>,
  ): OrOrOrderQuery<Def> | null {
    const field = this.nestedField(helpers, params.field);
    return field ? this.applyOperator(field, params) : null;
  }

  /** Resolves a one to four segment field path onto the fixed arity `field` overloads. */
  private nestedField(
    helpers: SaurusHelper<Def>,
    field: AbstractQueryParams<Def>['field'],
  ): (QueryPrimitiveField<Def> & QueryArrayField<Def>) | null {
    switch (field?.length) {
      case 4:
        return helpers.field(field[0], field[1], field[2], field[3]) as QueryPrimitiveField<Def> & QueryArrayField<Def>;
      case 3:
        return helpers.field(field[0], field[1], field[2]) as QueryPrimitiveField<Def> & QueryArrayField<Def>;
      case 2:
        return helpers.field(field[0], field[1]) as QueryPrimitiveField<Def> & QueryArrayField<Def>;
      case 1:
        return helpers.field(field[0] as QueryFieldKey1<Def>) as QueryPrimitiveField<Def> & QueryArrayField<Def>;
      default:
        return null;
    }
  }

  /**
   * Dispatches the operator named at runtime. Indexing the field selector with a `keyof` union
   * would demand the intersection of every member's parameter, which no value inhabits, so each
   * operator group spells out the value shape it accepts.
   */
  private applyOperator(
    field: QueryPrimitiveField<Def> & QueryArrayField<Def>,
    params: AbstractQueryParams<Def>,
  ): OrOrOrderQuery<Def> {
    const { operator, value } = params;
    switch (operator) {
      case 'lt':
      case 'lte':
      case 'eq':
      case 'not':
      case 'gt':
      case 'gte':
        return field[operator](value as QueryFieldValue<Def>);
      case 'in':
      case 'notIn':
        return field[operator](value as QueryFieldValue<Def>[]);
      case 'contains':
        return field.contains(value as Parameters<QueryArrayField<Def>['contains']>[0]);
      case 'containsAny':
        return field.containsAny(value as Parameters<QueryArrayField<Def>['containsAny']>[0]);
      case 'order':
        // typesaurus reads a string first argument as the direction and anything else as cursors.
        return typeof value === 'string'
          ? field.order(value as OrderDirection)
          : field.order(value as OrderCursors<Def>);
      default:
        // Matches today's `field[undefined](...)`, which throws rather than silently dropping the query.
        throw new Error(`Unsupported query operator: ${String(operator)}`);
    }
  }
}
